import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Alert, Card, Flex, Paragraph, spaceMixin } from '@zillow/constellation';
import { useDispatch } from 'react-redux';

import './index.scss';

import BackLink from './components/BackLink';
import LeasesPaymentsUpsell from './LeasesPaymentsUpsell/LeasesPaymentsUpsell';
import ScreeningBrownoutAlert from './ScreeningBrownoutAlert';
import Applicant from './components/Applicant';
import ApplicationRollup from './ApplicationRollup/ApplicationRollup';
import ComplianceMessagingProvider from '../../contexts/providers/ComplianceMessagingProvider';
import LocalLandlordAdvisory from './LocalLandlordAdvisory';
import ApplicationSummaryBadge from './ApplicationSummaryBadge';

import FormatLogic from '../../utils/formatLogicUtil';
import useQueryVariables from '../../hooks/useQueryVariables';
import LoadingScreen from '../../components/LoadingScreen/LoadingScreen';
import { PAGE_DATA_ERROR } from '../../constants/stringConstants';
import { PageHeader } from './PageHeader';
import { ApplicationSummaryContextProvider } from '../../contexts/ApplicationSummaryContext';
import { useZgGraphqlQuery } from '../../hooks/useGraphqlQuery';
import { APPLICATION_SUMMARY_QUERY } from '../../constants/queries';
import { fetchIdVerificationStatus, fetchTermsStatus } from '../../actions/landlordActionCreators';
import { applicationOverallStatusTypes } from '../../constants/statusConstants';

const StyledCard = styled(Card)`
  :not(:last-child) {
    margin-bottom: ${spaceMixin('lg')};
  }
`;

function ApplicationSummary({ applicants = [] }) {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchTermsStatus());
    dispatch(fetchIdVerificationStatus());
    window.scrollTo(0, 0);
  }, [dispatch]);
  const { applicationId, propertyId: listingAlias } = useQueryVariables();
  const { isLoading, isError, data, refetch } = useZgGraphqlQuery(APPLICATION_SUMMARY_QUERY, {
    listingAlias,
    applicationId,
  });

  useEffect(() => {
    // no need to fetch on initial mount but refetch on every subsequent mount to prevent stale data
    // ex: user declines application and uses react router links to get back to this page, we need to refetch
    // so the application reflects decline status
    if (!isLoading) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return <LoadingScreen className="rental-application__loading-screen--transparent-bg" show isLoading />;
  }

  if (isError) {
    return <Alert appearance="warning">{PAGE_DATA_ERROR}</Alert>;
  }
  const isApplicationAccepted =
    data.rentalListing.landlordRentalApplication.overallStatus === applicationOverallStatusTypes.APPROVED;
  const isApplicationDeclined =
    data.rentalListing.landlordRentalApplication.overallStatus === applicationOverallStatusTypes.DECLINED;
  const acceptedTimestamp = data.rentalListing.landlordRentalApplication.invitedToMoveInDateTime;
  const applicationAcceptedDateMessage = isApplicationAccepted
    ? `This application was approved ${FormatLogic.monthDayYear(acceptedTimestamp)}, ${FormatLogic.localTimeFromDate(
        acceptedTimestamp,
      )}`
    : null;

  return (
    <ComplianceMessagingProvider>
      <ApplicationSummaryContextProvider
        value={{
          applicants,
          refetchApplication: refetch,
          isApplicationDeclined,
          ...data.rentalListing,
        }}
      >
        <div className="ApplicationSummary">
          <BackLink />
          <ScreeningBrownoutAlert />
          <Flex paddingX={{ default: 'sm', lg: 0 }}>
            <PageHeader />
            <ApplicationSummaryBadge />
            {applicationAcceptedDateMessage && (
              <Paragraph data-test="application-approval-timestamp-message" marginBottom="sm">
                {applicationAcceptedDateMessage}
              </Paragraph>
            )}
          </Flex>
          <LeasesPaymentsUpsell />
          <StyledCard cardType="secondary" padding="md">
            <ApplicationRollup />
          </StyledCard>
          <Applicant />
          <LocalLandlordAdvisory />
        </div>
      </ApplicationSummaryContextProvider>
    </ComplianceMessagingProvider>
  );
}

ApplicationSummary.propTypes = {
  applicants: PropTypes.array,
};

export default ApplicationSummary;
